import React, { useEffect, useCallback } from 'react';
import Select from '../../common/Select.jsx';
import { DATA_TYPE_PROP_MAP } from '../../common/AgGrid/index.jsx';
import Help from '../../common/Help.jsx';
import DateInput from '../../common/DateInput.jsx';
import ToggleSwitch from '../../common/ToggleSwitch.jsx';
import { Col, Row } from '../../bootstrap/index.jsx';
import { update } from '../../../helpers/index.js';
import {
  parseValue,
  numberFormatOptions,
} from './settings.js'

import {default as Tooltip} from '../../common/Tooltip.jsx';

import {
  resolveNumberFormat,
  NUMBER_FORMATS,
  DATE_FORMATS
} from '../../../helpers/parsers.js';
import useDynamicValueGetters from './useDynamicValueGetters.js';
import { FaIcon } from '../../common/Icons.jsx';


const DateFormatOptionLabel = (value) => <>
  {value.format} <small className='description'>e.g. {value.example()}</small>
</>;

const dateFormatOptions = [
  ...[{label: 'Auto', value: 'auto'}],
  ...DATE_FORMATS.map(value => ({label: <DateFormatOptionLabel {...value} />, value: value.format}))
];

const SET_VALUE_OPTIONS = [
  'leave_blank', 
  'default_value', 
  'excel_column'
].map(v => ({value: v, label: I18n.t(v)}));

const UNIQUE_LIMIT = 30;

const ROW_PREPROCESS_LIMIT = 10000; // Larger preprocess will crash performance

export default function ColumnImportConfig({
    column,
    xlsWorkBook,
    settings,
    updateColumnImportConfig,
    gridRef,
    toggleGrid,
    validate
}){
  const importConfig = settings.columns[column.accessor] || {};

  // Modify the visibility and headerName for the target column
  const toggleColumnVisibility = React.useCallback(() => {
    toggleGrid('datagrid', {selectedColumn: column.accessor});
  }, [column]);

  // Update value getters on config changes 
  useDynamicValueGetters(column, importConfig, gridRef);

  // When the column selection changes make the grid highlight it
  useEffect(() => {
    toggleColumnVisibility();
  }, [column])

  // Changes that should trigger column validations
  useEffect(() => {
    validate(column, importConfig);
  }, [
    importConfig.set_value,
    importConfig.default_value,
    importConfig.ignore_blanks, 
    importConfig.excel_column, 
    importConfig.mapping
  ])

  const handleValueChange = (updatedConfig) => {
    updateColumnImportConfig(column.accessor, {...importConfig, ...updatedConfig})
  };

  const handleMappingChange = (from, to, idx) => {
    let newImportConfig = {...importConfig}

    // The mapping can have a format [empty × 2, {…}], use slice to prerve empty instead of adding nulls/undefined
    let mapping = newImportConfig.mapping.slice(); 
    mapping[idx] = {
      ...(mapping[idx] || {}),
      from,
      to
    };

    newImportConfig = { ...newImportConfig, mapping };

    updateColumnImportConfig(column.accessor, newImportConfig)
  };

  const getUniqueCellValues = (excelColumn, selection_options) => {
    if (!excelColumn) return [];
    const uniques = new Set();
    const uniqueLim = Math.max(Math.floor((selection_options || []).length)*0.1, UNIQUE_LIMIT);
    gridRef.current.api.forEachNode((rowNode, index) => {
      if (!rowNode.displayed) return;
      if (uniques.size < uniqueLim) {
        uniques.add(parseValue(rowNode.data[excelColumn - 1]));
      }
    })
    return [...uniques];
  }

  const handleExcelColumnChange = (col_idx) => {
    let newValue = {excel_column: col_idx};

    // Set the resolved number format
    if (['integer', 'decimal'].includes(column.data_type)) {
      const resolvedType = xlsWorkBook.resolved_column_types[col_idx - 1];
      if (resolvedType?.n !== undefined) newValue = {...newValue, number_format: resolvedType.n};
    }

    // Set the resolved date format
    if (['date'].includes(column.data_type)) {
      const resolvedType = xlsWorkBook.resolved_column_types[col_idx - 1];
      if (resolvedType?.d !== undefined) newValue = {...newValue, date_format: resolvedType.d};
    }

    if (column.is_selection) {
      newValue = {...newValue, mapping: autoMapOptions((column.selection_options || []).length)}
    }

    handleValueChange(newValue);

    
  };

  const handleIgnoreBlanksChange = (val) => {
    handleValueChange({ignore_blanks: val});
  };

  const handleDateFormatChange = (val) => {
    handleValueChange({date_format: val});
  };

  const handleNumberFormatChange = (val) => {
    handleValueChange({number_format: val});
  };

  const handlePreprocessFunctionChange = (preprocess_function) => {
    handleValueChange({preprocess_function: preprocess_function});
  }

  // Resolve unique fields for each column (up-to-100 items) for mapping later
  const uniqueValues = useCallback((selection_options) => getUniqueCellValues(importConfig.excel_column, selection_options), [importConfig.excel_column])

  const resolve_target = (key) => {
    let match = column.selection_options.find(opt => {
      if (opt.label == key) {
        return true;
      }
    })
    return match?.value || null;
  }

  const autoMapOptions = (selection_options) => {
    const mapping = uniqueValues(selection_options).map(mapped_from => ({
      from: mapped_from,
      to: resolve_target(mapped_from)
    }))
    return mapping;
  }

  const handleAutoMap = (selection_options) => {
    let newImportConfig = { ...importConfig, mapping: autoMapOptions(selection_options) };
    updateColumnImportConfig(column.accessor, newImportConfig)
  }

  

  return (<>
    <div className='ml-1 pt-1'>
      <div className='form-group'>
        <label>{I18n.t('set_value')}</label>
        <Select
          options={SET_VALUE_OPTIONS}
          value={importConfig.set_value}
          onChange={(val) => handleValueChange({set_value: val})}
        />
      </div>

      { importConfig.set_value == 'default_value' &&
        <div className='form-group'>
          { column.is_selection ?
            <Select
              options={column.selection_options}
              value={importConfig.default_value}
              onChange={(val) => handleValueChange({default_value: val})}
            />
            : 
            <Tooltip title={I18n.t('excel_import_input_description')} style={{display: 'block'}}>
              {
                ['date', 'timestamp'].includes(column.data_type) ?
                <DateInput
                  defaultValue={importConfig.default_value}
                  pickerOptions={{format: DATA_TYPE_PROP_MAP[column.data_type].cellEditorParams['format']}}
                  onChange={(val) => handleValueChange({default_value: val})}
                />
                :
                  <input
                    className='form-control'
                    value={importConfig.default_value}
                    onChange={(e) => handleValueChange({default_value: e.target.value})}
                  />
              }
            </Tooltip>
          }
        </div>
      }

      { importConfig.set_value == 'excel_column' &&
        <>
          <div className='form-group'>
            <label>{I18n.t('excel_column')}</label>
            <Select
              options={xlsWorkBook.ws_column_names}
              value={importConfig.excel_column}
              onChange={handleExcelColumnChange}
            />
          </div>
          { ['date', 'timestamp'].includes(column.data_type) ?
            <div className='form-group'>
              <label>{I18n.t('select_date_format', { scope: 'di' })}</label>
              <Select
                options={dateFormatOptions}
                value={importConfig.date_format}
                onChange={handleDateFormatChange}
              />
            </div>
            :
            ['decimal'].includes(column.data_type) &&
              <div className='form-group'>
                <label>{I18n.t('select_number_format')}</label>
                <Select
                  options={numberFormatOptions}
                  value={importConfig.number_format}
                  onChange={handleNumberFormatChange}
                />
              </div>
          }

          { column.is_selection && importConfig.excel_column &&
            <div className='form-group' style={{marginBottom: '3rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between'}}>
                <label style={{marginBottom: 0}}>{I18n.t('mapping')}</label>
                <Tooltip title={I18n.t('auto_mapping_tooltip')}>
                  
                  <a className='description' style={{cursor: 'pointer'}} onClick={() => handleAutoMap(column.selection_option)}>
                    <FaIcon type='wand-magic-sparkles' style={{marginRight: '0.25rem'}} />
                    {I18n.t('auto').toUpperCase()}
                  </a>
                </Tooltip>
              </div>
              {
                uniqueValues(column.selection_option).map((mapped_from, i) => {
                  var mapping = (importConfig.mapping || {})[i] || {};
                  return <>
                    <div key={`mapping-${column.accessor}-${i}`} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem'}}>
                        <div style={{flex: 1, maxWidth: 'calc(50% - 15px)'}}>
                          <Tooltip title={mapped_from} className='form-control text-truncate bg-light'>
                              {mapped_from}
                          </Tooltip>
                          {i == uniqueValues(column.selection_option).length - 1 && <span className='description'>{I18n.t('excel_field')}</span>}

                        </div>
                        <FaIcon type='arrow-right' className={i == uniqueValues(column.selection_option).length - 1 ? 'mb-2' : ''} />
                        <div style={{flex: 1, maxWidth: 'calc(50% - 15px)'}}>
                          <Select
                            options={column.selection_options}
                            value={mapping.from == mapped_from ? mapping.to : ''}
                            onChange={(mapped_to) => handleMappingChange(mapped_from, mapped_to, i)}
                          />
                          {i == uniqueValues(column.selection_option).length - 1 && <span className='description'>{I18n.t('grid_field')}</span>}
                        </div>
                    </div>
                  </>
                })
              }
            </div>
          }

          { importConfig.set_value == 'excel_column' &&
            <div className='form-group'>
              <label>{I18n.t('preprocess_function')}</label>
              <Help text={xlsWorkBook?.total_row_count  > ROW_PREPROCESS_LIMIT ? I18n.t('preprocess_function_limit') : I18n.t("preprocess_function_help")} trigger="hover" className='' />

              <input
                type="text"
                className="form-control"
                defaultValue={importConfig.preprocess_function}
                onBlur={(e) => handlePreprocessFunctionChange(e.target.value)}
                autoComplete='off'
                disabled={xlsWorkBook?.total_row_count  > ROW_PREPROCESS_LIMIT}
              />
            </div>
          }

          <div className='form-group'>
            <label>{I18n.t('ignore_blanks')}</label>
            <Help text={I18n.t("ignore_blanks_help")} trigger="hover" className='' />
            <br/>
            <ToggleSwitch
              name='ignore_blanks'
              isOn={importConfig.ignore_blanks}
              handleToggle={() => handleIgnoreBlanksChange(!importConfig.ignore_blanks)}
            />
          </div>
        </>
      }
    </div>
  </>)
};

